import React from "react";
import {
  CCard,
  CCol,
  CProgress,
  CProgressBar,
  CRow,
  CInput,
} from "@coreui/react";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import NumberFormat from "react-number-format";
import { ceilToDecimalPlaces } from "src/utils/ceilToDecimalPlaces";
import {
  getPriorityColor,
  getRiskClassifierClass,
  getStatusColor,
  ItemBadge,
} from "./ProjectTask";
import "./ProjectDetails.css";

export const ProjectPowAmount = ({
  phaseItem,
  phaseIndex,
  isExpanded,
  isSaveDisabled,
  onTogglePhase,
  onDragStart,
  onDragEnter,
  onDrop,
  onPriorityChange,
  onAutoSave,
  onPowAmountChange,
}) => {
  const progressPercentage = ceilToDecimalPlaces(
    phaseItem.ProgressPercentage,
    2,
  );

  const totalBudget = ceilToDecimalPlaces(
    phaseItem.Total_Budget,
    2,
  );

  const totalCost = ceilToDecimalPlaces(
    phaseItem.Total_POW_Amount,
    2,
  );

  return (
    <CCol xs="12" sm="12" md="12" lg="12" style={{
      padding: "1px", marginTop: "20px",
    }}

    >
      {/* PHASE CARD */}
      <CCard
        style={{
          padding: "20px",
          marginBottom: "0",
          border: (phaseItem.Total_POW_Amount ?? 0) === 0 ? "1px solid #e53e3e" : undefined,
        }}
        className={`shadow-sm ${(phaseItem.Total_POW_Amount ?? 0) === 0 ? "border-danger" : ""}`}
        onClick={() => onTogglePhase(phaseItem.Phase)}
      >
        <CRow>
          <CCol xs="12" sm="12" md="4" lg="3" xl="3" style={{ marginTop: "10px" }}>
            <span className="float-left circle-number">
              <span>{phaseIndex + 1}</span>
            </span>

            <div className="sow-label">
              {phaseItem.Phase?.toUpperCase()}
            </div>
          </CCol>

          <CCol xs="12" sm="12" md="1" lg="1" className="float-left text-center" style={{ paddingTop: "10px" }}
          >
            <CCol xs="12" sm="12" md="12" lg="12">
              <ItemBadge
                underBudget={phaseItem.under_budget}
                delayed={phaseItem.delayed}
                incomplete={phaseItem.incomplete}
              />
            </CCol>
          </CCol>

          <CCol md="7" lg="6" style={{ paddingLeft: "40px" }}>
            <CCol md="12" lg="12" className="float-right">
              <CRow>
                <CCol xs="6" sm="6" md="6" lg="6">
                  <span className="budget-label">Total Budget</span>

                  <div className="budget-value">
                    <div style={{ textAlign: "left" }}>
                      ₱
                      <NumberFormat
                        value={totalBudget}
                        displayType="text"
                        thousandSeparator
                        decimalScale={2}
                        fixedDecimalScale
                        style={{ marginLeft: "5px" }}
                      />
                    </div>
                  </div>
                </CCol>

                <CCol xs="6" sm="6" md="6" lg="6">
                  <div className="float-right">
                    <div
                      className="budget-label"
                      style={{ textAlign: "right" }}
                    >
                      Total Cost
                    </div>

                    <div className="budget-value">
                      <div style={{ textAlign: "right" }}>
                        ₱
                        <NumberFormat
                          value={totalCost}
                          displayType="text"
                          thousandSeparator
                          decimalScale={2}
                          fixedDecimalScale
                          style={{ marginLeft: "5px" }}
                        />
                      </div>
                    </div>
                  </div>
                </CCol>
              </CRow>

              <CProgress size="md">
                <CProgressBar
                  color="aimhi-textlight-neutral"
                  value={totalBudget === 0 ? 0 : totalCost}
                  max={totalBudget === 0 ? 1 : totalBudget}
                  showPercentage
                  className="text-white"
                />
              </CProgress>
            </CCol>
          </CCol>

          <CCol xs="12" sm="12" md="2" lg="2" className="align-center">
            <div className="circular-progress-size">
              <CircularProgressbarWithChildren
                className="circular-progress-size"
                value={progressPercentage}
                text={`${progressPercentage}%`}
                styles={{
                  path: {
                    stroke: "#351D75",
                    strokeLinecap: "butt",
                    transition:
                      "stroke-dashoffset 0.5s ease 0s",
                    transformOrigin: "center center",
                    circleRatio: "0",
                    strokeWidth: "-10",
                  },
                  trail: {
                    stroke: "#9c9c9c",
                    strokeLinecap: "butt",
                    transform: "rotate(0.25turn)",
                    transformOrigin: "center center",
                    circleRatio: "0",
                    strokeWidth: "-10",
                  },
                  text: {
                    fill: "#23303D",
                    fontSize: "20px",
                    fontWeight: "700",
                  },
                  background: {
                    fill: "#54c2a2",
                  },
                }}
              />
            </div>

            <span className="status-label align-text-mobile">
              Progress
            </span>
          </CCol>
        </CRow>
      </CCard>

      {/* TASKS */}
      <CCol xs="12" sm="12" md="12" lg="12" style={{ padding: "0px" }}>
        <div className={`transition-all duration-300 ${isExpanded ? "opacity-100" : "max-h-0 overflow-hidden opacity-0"}`}>
          {(phaseItem.Task || []).map(
            (task, taskIndex) => {
              const priorityColor =
                getPriorityColor(task.Priority);

              const statusColor =
                getStatusColor(task.Status);

              const classifierClass =
                getRiskClassifierClass(task.risk_score);

              const powAmount = task.pow_amount ?? 0;

              return (
                <CCol xs="12" sm="12" md="12" lg="12" xl="12" key={`${task.sow_id}-${taskIndex}`}
                  style={{ padding: "0px" }}
                >
                  <div className={classifierClass}></div>

                  <CCard id={`task-${task.sow_id}`} className="task-card" style={{ marginLeft: "30px", }}>
                    <CRow>
                      {/* SOW */}
                      <CCol xs="12" sm="12" md="12" lg="3" id="Task">
                        <span className="task-label">{task.SOW}</span>
                      </CCol>

                      {/* PRIORITY / STATUS / DATES */}
                      <CCol xs="12" sm="12" md="12" lg="6" className="text-center" id="Priority, Status and dates">
                        <CRow>
                          {/* PRIORITY */}
                          <CCol xs="12" sm="12" md="12" lg="3" id="Priority" style={{ padding: "0px" }}>
                            <span className="top-label">Priority</span>

                            <br />

                            <select
                              className={`${priorityColor} select-design ${priorityColor === "yellow" ? "text-dark" : ""}`}
                              onChange={(e) => onPriorityChange(e.target.value, task._originalTaskIndex ?? taskIndex, phaseIndex)}
                              onBlur={(e) =>
                                onAutoSave(
                                  {
                                    id: task.id,
                                    priority: e.target.value,
                                  },
                                  task._originalTaskIndex ?? taskIndex,
                                  phaseIndex,
                                )
                              }
                              value={task.Priority}
                              disabled={isSaveDisabled}
                            >
                              <option style={{ backgroundColor: "white", }} value="" />
                              <option style={{ backgroundColor: "#B42A19", color: "white", }} value="high">High</option>
                              <option style={{ backgroundColor: "#FDFE54", color: "black", }} value="moderate">Moderate</option>
                              <option style={{ backgroundColor: "#3A7D21", color: "white" }} value="low">Low</option>
                            </select>
                          </CCol>
                          {/* TARGET START / ACTUAL START */}
                          <CCol xs="12" sm="12" md="12" lg="3" className="text-center" id="Target Start and Actual Start">
                            <CRow>
                              <CCol xs="12" sm="12" md="12" lg="12" id="Target Start">
                                <span className="top-label ">
                                  Target Start
                                </span>

                                <br />

                                <span
                                  className="text-xs text-gray-700"
                                >
                                  {task.StartDate || "---"}
                                </span>
                              </CCol>

                              <CCol xs="12" sm="12" md="12" lg="12" id="Actual Start">
                                <span className="top-label">
                                  Actual Start
                                </span>

                                <br />

                                <span
                                  className="text-xs text-gray-700"
                                >
                                  {task.actual_start ||
                                    "---"}
                                </span>
                              </CCol>
                            </CRow>
                          </CCol>

                          {/* TARGET END / ACTUAL END */}
                          <CCol xs="12" sm="12" md="12" lg="3" className="text-center" id="Target End and Actual End">
                            <CRow>
                              <CCol xs="12" sm="12" md="12" lg="12" id="Target End">
                                <span className="top-label">
                                  Target End
                                </span>

                                <br />

                                <span
                                  className="text-xs text-gray-700"
                                >
                                  {task.EndDate || "---"}
                                </span>
                              </CCol>

                              <CCol xs="12" sm="12" md="12" lg="12" id="Actual End">
                                <span className="top-label">
                                  Actual End
                                </span>

                                <br />

                                <span
                                  className="text-xs text-gray-700"
                                >
                                  {task.actual_end ||
                                    "---"}
                                </span>
                              </CCol>
                            </CRow>
                          </CCol>

                          {/* Estimated Cost */}
                          <CCol xs="12" sm="12" md="12" lg="3" id="Estimate Amount" style={{ padding: "0px" }}
                          >
                            <span className="top-label">Estimated Cost</span>
                            <div className="budget-value text-center" style={{ marginTop: "2px" }}>
                              ₱
                              <NumberFormat
                                value={ceilToDecimalPlaces(task.estimate_amount ?? 0, 2)}
                                displayType="text"
                                thousandSeparator
                                decimalScale={2}
                                fixedDecimalScale
                                style={{ marginLeft: "5px" }}
                              />
                            </div>
                          </CCol>


                        </CRow>
                      </CCol>

                      {/* POW AMOUNT */}
                      <CCol xs="12" sm="12" md="12" lg="3" className="text-center" id="POW Amount">
                        <div className="flex w-full flex-col items-center px-2">
                          <span className="top-label mb-1">
                            POW Amount
                          </span>

                          <div className=" w-full max-w-[200px]">
                            <CInput
                              type="number"
                              min="0"
                              step="0.01"
                              value={powAmount}
                              disabled={isSaveDisabled}
                              placeholder="0.00"
                              onChange={(e) => {
                                const value = e.target.value;
                                if (typeof onPowAmountChange === "function") {
                                  onPowAmountChange(
                                    value,
                                    task._originalTaskIndex ?? taskIndex,
                                    phaseIndex,
                                  );
                                }
                              }}
                              className={`w-full rounded-md border ${powAmount == 0 ? 'border-aimhi-warning' : 'border-gray-300'} bg-white py-2 pl-8 pr-3 text-right text-sm font-medium text-gray-800 shadow-sm outline-none transition  disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400`}
                            />
                          </div>
                        </div>
                      </CCol>
                    </CRow>
                  </CCard>
                </CCol>
              );
            },
          )}
        </div>
      </CCol>
    </CCol>
  );
};
